import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Target, LineChart, Users, MessageSquare, Layers, Compass, CheckCircle2 } from "lucide-react";

const modules = [
  { icon: Compass, name: "Vision / V-TO", desc: "10-yr target, core focus, 3-yr picture, 1-yr plan — all in one operating doc." },
  { icon: LineChart, name: "Scorecard", desc: "Weekly KPI tracking. Red / Yellow / Green at a glance." },
  { icon: Target, name: "Rocks", desc: "90-day priorities. Owner, status, no surprises." },
  { icon: CheckCircle2, name: "Issues List", desc: "Identify, Discuss, Solve. Kill the noise, not the signal." },
  { icon: Users, name: "Accountability Chart", desc: "Not an org chart. Seats & roles that actually work." },
  { icon: Layers, name: "Process Library", desc: "AI-generated SOPs. From tribal knowledge to written system." },
  { icon: MessageSquare, name: "AI Ops Coach", desc: "Claude Sonnet 5, wired to your live business context." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F9F9F8] text-neutral-900">
      {/* Nav */}
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-[#F9F9F8]/80 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-home-link">
            <div className="w-6 h-6 bg-[#E54D2E]" />
            <span className="font-display text-lg font-bold tracking-tight">Runwell HQ</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm">
            <a href="#modules" className="hover:text-[#E54D2E]">Modules</a>
            <a href="#how" className="hover:text-[#E54D2E]">How it works</a>
            <a href="#pricing" className="hover:text-[#E54D2E]">Pricing</a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login-btn">Sign in</Button></Link>
            <Link to="/register">
              <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white btn-lift rounded-sm" data-testid="nav-signup-btn">
                Start free <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24">
          <div className="mono text-xs tracking-widest uppercase text-neutral-500 mb-6">EST. 2026 — OPERATING SYSTEM FOR SMALL BUSINESS</div>
          <h1 className="font-display font-black tracking-tighter text-6xl md:text-8xl leading-[0.9] max-w-5xl">
            Run your 40-person company<br />
            <span className="text-[#E54D2E]">like a 400-person one.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-neutral-600 leading-relaxed">
            Runwell HQ installs the systems, KPIs, meeting rhythms and processes that great operators use — in one online workspace,
            with an AI Ops Coach that knows your business.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register">
              <Button size="lg" className="bg-[#E54D2E] hover:bg-[#C93E22] text-white rounded-sm btn-lift px-6" data-testid="hero-cta-signup">
                Start free — no credit card
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="rounded-sm border-neutral-300 btn-lift" data-testid="hero-cta-signin">
                See a live demo
              </Button>
            </Link>
          </div>

          {/* Bento */}
          <div className="mt-20 grid grid-cols-12 gap-4 auto-rows-[120px]">
            <div className="col-span-12 md:col-span-8 row-span-3 bg-neutral-900 text-white p-8 relative overflow-hidden">
              <div className="mono text-xs text-neutral-400 mb-2">01 — WEEKLY SCORECARD</div>
              <div className="font-display text-3xl md:text-4xl leading-tight">Every metric that matters, one view. Red means fix it this week.</div>
              <div className="mt-6 grid grid-cols-4 gap-2 max-w-lg">
                {[
                  { l: "MRR", v: "$142K", c: "bg-green-500" },
                  { l: "NPS", v: "62", c: "bg-green-500" },
                  { l: "CAC", v: "$412", c: "bg-yellow-500" },
                  { l: "Churn", v: "3.1%", c: "bg-red-500" },
                ].map((k) => (
                  <div key={k.l} className="border border-neutral-700 p-3">
                    <div className="flex items-center gap-1.5 text-[10px] text-neutral-400 uppercase mono">
                      <span className={`w-1.5 h-1.5 ${k.c}`} />{k.l}
                    </div>
                    <div className="mono text-xl mt-1">{k.v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-6 md:col-span-4 row-span-2 bg-white border border-neutral-200 p-6">
              <div className="mono text-xs text-neutral-500 mb-2">02 — QUARTERLY ROCKS</div>
              <div className="font-display text-xl leading-tight">90-day priorities. Owned. Tracked. Done.</div>
              <div className="mt-4 space-y-1.5">
                {["Launch enterprise tier", "Hire 2 AEs", "Ship API v2"].map((r, i) => (
                  <div key={r} className="flex items-center gap-2 text-sm">
                    <span className={`w-1.5 h-1.5 ${i===2?"bg-yellow-500":"bg-green-500"}`} />
                    <span className="text-neutral-700">{r}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-6 md:col-span-4 row-span-1 bg-[#E54D2E] text-white p-5">
              <div className="mono text-xs opacity-80">03 — AI COACH</div>
              <div className="font-display text-lg leading-tight mt-1">"Your churn is up because your onboarding NPS dropped in week 2."</div>
            </div>
            <div className="col-span-12 md:col-span-8 row-span-1 bg-white border border-neutral-200 p-5 flex items-center justify-between">
              <div>
                <div className="mono text-xs text-neutral-500">04 — L10 MEETING</div>
                <div className="font-display text-lg">Same agenda every week. 90 minutes. Never late.</div>
              </div>
              <div className="mono text-2xl">89:12</div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="mono text-xs tracking-widest uppercase text-neutral-500 mb-4">THE MODULES</div>
          <h2 className="font-display text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">Eight tools. One rhythm. Zero spreadsheets.</h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-neutral-200 grid-borders">
            {modules.map((m) => (
              <div key={m.name} className="p-8 bg-white hover:bg-neutral-50 transition-colors">
                <m.icon className="h-6 w-6 text-[#E54D2E]" strokeWidth={1.5} />
                <div className="font-display text-xl font-bold mt-6">{m.name}</div>
                <div className="text-sm text-neutral-600 mt-2 leading-relaxed">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <div className="mono text-xs tracking-widest uppercase text-neutral-500 mb-4">THE RHYTHM</div>
              <h2 className="font-display text-5xl font-bold tracking-tight">Weekly, quarterly, yearly. On rails.</h2>
              <p className="text-neutral-600 mt-6 leading-relaxed">
                Every serious operating system — EOS, Scaling Up, OKRs — depends on cadence. Runwell HQ enforces yours,
                and the AI coach flags what's slipping before it becomes a fire.
              </p>
            </div>
            <div className="space-y-6">
              {[
                { t: "Weekly", d: "L10 meeting. Scorecard review. Rocks check-in. IDS on top issues." },
                { t: "Quarterly", d: "Set new Rocks. Update the 1-year plan. Refresh KPIs." },
                { t: "Yearly", d: "Refresh the V/TO. Reset core focus. Look 3 years out." },
              ].map((s, i) => (
                <div key={s.t} className="flex gap-4 border-t border-neutral-200 pt-6">
                  <div className="mono text-3xl text-[#E54D2E] w-12">0{i+1}</div>
                  <div>
                    <div className="font-display font-bold text-xl">{s.t}</div>
                    <div className="text-sm text-neutral-600 mt-1">{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="mono text-xs tracking-widest uppercase text-neutral-500 mb-4">PRICING</div>
          <h2 className="font-display text-5xl font-bold tracking-tight">Simple. Priced per company, not per seat.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-0 border border-neutral-200 bg-white">
            {[
              { n: "Solo Operator", p: "Free", d: "For the first 30 days. Every module. No card.", cta: "Start" },
              { n: "Team", p: "$149 /mo", d: "Up to 25 seats. AI Coach included.", cta: "Start", feature: true },
              { n: "Company", p: "$399 /mo", d: "Up to 100 seats. Priority support. Custom SOPs.", cta: "Talk to us" },
            ].map((t) => (
              <div key={t.n} className={`p-8 border-r border-neutral-200 last:border-r-0 ${t.feature ? "bg-neutral-900 text-white" : ""}`}>
                {t.feature && <div className="mono text-xs text-[#E54D2E] mb-3">MOST POPULAR</div>}
                <div className="font-display text-2xl font-bold">{t.n}</div>
                <div className="mono text-4xl mt-6">{t.p}</div>
                <div className={`text-sm mt-3 ${t.feature ? "text-neutral-300" : "text-neutral-600"}`}>{t.d}</div>
                <Link to="/register">
                  <Button className={`mt-8 w-full rounded-sm btn-lift ${t.feature ? "bg-[#E54D2E] hover:bg-[#C93E22] text-white" : "bg-neutral-900 hover:bg-neutral-800 text-white"}`} data-testid={`pricing-cta-${t.n.toLowerCase().replace(/\s/g,'-')}`}>
                    {t.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#E54D2E]" />
            <span className="font-display font-bold">Runwell HQ</span>
            <span className="mono text-xs text-neutral-500 ml-4">© 2026 — All systems operational</span>
          </div>
          <div className="mono text-xs text-neutral-500">Built for operators. Powered by Claude Sonnet 5.</div>
        </div>
      </footer>
    </div>
  );
}
